import { Component, Input, OnInit, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { Country, SRSBallotStatus } from '../../../../../shared/constants/srs-ballot.constants';
import { IDonutChartItem } from '../../../../controls/donut-chart/donut-chart.component';
import { ISrsBallotDetail } from '../../../shared/models/sr/srs-ballot-details';
import { ISrsBallotVote, SrsVoteId, srsVoteOption } from '../../../shared/models/sr/srs-vote';

@Component({
    selector: 'app-srs-eligible-voters',
    templateUrl: './srs-eligible-voters.component.html',
    styleUrl: './srs-eligible-voters.component.scss',
    standalone: false
})
export class SrsEligibleVotersComponent implements OnInit {
  @Output() afterSave = new EventEmitter<string>();
  @Input() srsBallotDetail: ISrsBallotDetail | null = null;
  ballotVotes: ISrsBallotVote[] = [];
  isJoint: boolean = false;
  srsBallotStatus: string = ``;
  ballotId: string = ``;
  ballotResolution: string = ``;

  isShowingLocations: boolean = false;
  data: IDonutChartItem[] = [];
  eligibleVotes: ISrsBallotVote[] = [];
  reviseCount: number = 0;
  withdrawCount: number = 0;
  reconfirmCount: number = 0;
  noneCount: number = 0;
  selectedLocation: string = 'aus-nz';
  ballotType = 'srsBallot';
  isConsensusVisible: boolean = false;
  isRefresh: boolean = true;
  hasMetConsensus: boolean = false;
  isClosedForVoting: boolean = false;
  isAllNoResponse: boolean = false;
  isUserPmOrAboveOrHoSD = false;
  majorityVote: any;
  voteTypes = [
    SrsVoteId.SVT_RECONFIRM,
    SrsVoteId.SVT_REVISE,
    SrsVoteId.SVT_WITHDRAW,
    SrsVoteId.SVT_NONE,
  ];

  subscriptions: Subscription = new Subscription();

  constructor(
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if(this.srsBallotDetail){
      this.srsBallotStatus = this.srsBallotDetail.status;
      this.ballotId = this.srsBallotDetail.ballotId;
      this.ballotResolution = this.srsBallotDetail.ballotResolution;
      this.isJoint = this.srsBallotDetail.isJoint;
      this.ballotVotes = this.srsBallotDetail.votes;
      if (this.ballotVotes && this.ballotVotes.length > 0) {
        this.checkConsensusVisible();
        this.filterEligibleVotes();
        this.getVoteDetails();
        this.checkNZCommittee();
      }

    }
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getVoteDetails() {
    const voteCounts = this.eligibleVotes.reduce(
      (counts: { [key: string]: number }, vote) => {
        const type = vote.voteTypeId;
        if (type) {
          counts[type] = (counts[type] || 0) + 1;
        }
        return counts;
      },
      {}
    );

    // Assign counts to your specific variables
    this.reconfirmCount = voteCounts[SrsVoteId.SVT_RECONFIRM] || 0;
    this.reviseCount = voteCounts[SrsVoteId.SVT_REVISE] || 0;
    this.withdrawCount = voteCounts[SrsVoteId.SVT_WITHDRAW] || 0;
    this.noneCount = voteCounts[SrsVoteId.SVT_NONE] || 0;

    if (voteCounts) {
      this.generateDonutChart(voteCounts);
    }
  }

  filterByLocation(): void {
    const filteredVotes = this.eligibleVotes.filter((vote) => {
      const voteCountry = vote.country.toLowerCase();
      if (this.selectedLocation === 'aus-nz') {
        this.isRefresh = true;
        return (
          Country.Australia.includes(voteCountry) ||
          Country.NewZealand.includes(voteCountry)
        );
      } else if (this.selectedLocation === 'aus') {
        this.isRefresh = true;
        return Country.Australia.includes(voteCountry);
      } else if (this.selectedLocation === 'nz') {
        this.isRefresh = false;
        return Country.NewZealand.includes(voteCountry);
      }
      return false;
    });

    const voteCounts = filteredVotes.reduce(
      (counts: { [key: string]: number }, vote) => {
        const type = vote.voteTypeId;
        if (type) {
          counts[type] = (counts[type] || 0) + 1;
        }
        return counts;
      },
      {}
    );

    this.generateDonutChart(voteCounts);
    this.cdr.detectChanges();
  }

  generateDonutChart(voteCounts: any): void {
    this.data = this.voteTypes.map((voteType) => ({
      label: srsVoteOption[voteType].text,
      value: voteCounts[voteType] || 0,
      color: srsVoteOption[voteType].color,
      icon: srsVoteOption[voteType].icon,
    }));
  }

  filterEligibleVotes() {
    this.eligibleVotes = this.ballotVotes.filter((vote) => vote.isEligible);
    const initialVoteCounts = this.eligibleVotes.reduce(
      (counts: { [key: string]: number }, vote) => {
        const type = vote.voteTypeId;
        if (type) {
          counts[type] = (counts[type] || 0) + 1;
        }
        return counts;
      },
      {}
    );

    const values = this.voteTypes.map((voteType) => ({
      label: srsVoteOption[voteType].text,
      value: initialVoteCounts[voteType] || 0,
      color: srsVoteOption[voteType].color,
      icon: srsVoteOption[voteType].icon,
    }));

    const largestItem = values.reduce(
      (maxItem, currentItem) => {
        return (currentItem.value ?? 0) > (maxItem.value ?? 0)
          ? currentItem
          : maxItem;
      },
      { value: 0 }
    );

    this.majorityVote = {
      ...largestItem,
      ballotId: this.ballotId,
    };
  }

  checkNZCommittee() {
    const hasNZCommitteeVotes = this.ballotVotes.find(
      (vote) =>
        vote.country && Country.NewZealand.includes(vote.country.toLowerCase())
    );
    this.isShowingLocations = hasNZCommitteeVotes != null && this.isJoint;
  }

  checkConsensusVisible() {
    this.isClosedForVoting =
      this.srsBallotStatus === SRSBallotStatus.ClosedForVoting;
    this.isConsensusVisible =
      this.srsBallotStatus === SRSBallotStatus.OpenForVoting ||
      this.isClosedForVoting;
  }

  handleConsensusChange(hasMetConsensus: boolean) {
    this.hasMetConsensus = hasMetConsensus;
    this.checkResponses();
  }

  checkResponses() {
    this.isAllNoResponse = this.eligibleVotes.every(
      (vote) => vote.voteTypeId === SrsVoteId.SVT_NONE
    );
  }

  onAfterSave(event: string) {
    this.afterSave.emit(event);
  }
}
